/**
 * Login Page with Manual Sign-In
 * =============================================================================
 * Provides manual Google and Email/Password authentication
 *
 * Features:
 * - Manual Google Sign-In (no FirebaseUI)
 * - Email/Password authentication
 * - Automatic redirect after successful login
 * - Clean, reliable, production-ready
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../config/firebase';
import { logger } from '../lib/logger';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendSignInLinkToEmail,
} from 'firebase/auth';
import { isEmailAllowed, getUnauthorizedMessage } from '../config/allowlist';
import { fetchSsoProvider, emailDomain } from '../api/ssoApi';

// The app root uses React.StrictMode. Reuse one in-flight read so its development
// setup→cleanup→setup cycle cannot consume the result in the cancelled first effect and
// then treat the second read as a fresh tile launch.
let redirectResultInFlight: ReturnType<typeof getRedirectResult> | null = null;
const getRedirectResultOnce = () => {
  redirectResultInFlight ??= getRedirectResult(auth);
  return redirectResultInFlight;
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [authError, setAuthError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [linkSentTo, setLinkSentTo] = useState<string | null>(null);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [redirectChecked, setRedirectChecked] = useState(false);

  // Redirect to home if already logged in. Gated on redirectChecked so this
  // cannot navigate away before getRedirectResult() has processed an
  // upstream IdP credential (see the bootstrap effect below).
  useEffect(() => {
    if (redirectChecked && !loading && user) {
      navigate('/', { replace: true });
    }
  }, [redirectChecked, user, loading, navigate]);

  // Single bootstrap effect: consume any pending redirect result exactly
  // once (StrictMode-safe via getRedirectResultOnce), then — only if there
  // was no result, no current user, and no error — honor a `?org=` deep
  // link by starting a new SSO redirect. Do not add a second `?org=`
  // effect; that races the pending redirect result and creates a loop.
  useEffect(() => {
    let cancelled = false;

    const bootstrapSso = async () => {
      setIsProcessing(true);
      try {
        const result = await getRedirectResultOnce();
        if (cancelled) return;
        if (result?.user) {
          // Pilot-only inspection seam: put a debugger breakpoint on the next line and
          // inspect credential?.idToken in memory. Never log or persist it.
          const credential = OAuthProvider.credentialFromResult(result);
          void credential;
          navigate('/', { replace: true });
          return;
        }
        if (auth.currentUser) {
          navigate('/', { replace: true });
          return;
        }

        const org = new URLSearchParams(window.location.search).get('org');
        if (org) {
          const providerId = await fetchSsoProvider({ org });
          if (cancelled) return;
          if (!providerId) {
            setAuthError('Enterprise SSO is not configured for this organization.');
            return;
          }
          await signInWithRedirect(auth, new OAuthProvider(providerId));
        }
      } catch (error: any) {
        if (cancelled) return;
        if (error?.code === 'auth/account-exists-with-different-credential') {
          setAuthError('An account already exists with this email via a different sign-in method.');
        } else {
          setAuthError(`SSO sign-in failed: ${error?.message || 'Please try again.'}`);
        }
      } finally {
        if (!cancelled) {
          // Do not retain a result/credential after the active effect consumed it.
          redirectResultInFlight = null;
          setRedirectChecked(true);
          setIsProcessing(false);
        }
      }
    };

    void bootstrapSso();
    return () => { cancelled = true; };
  }, [navigate]);

  // Email-first "Continue": look up whether this email's domain has
  // enterprise SSO configured. If so, redirect to the IdP. If discovery
  // succeeds with no provider, reveal the legacy password/email-link
  // fields. A transport/5xx/malformed-response failure surfaces an error
  // instead — it must never silently fall through to legacy login.
  const handleContinue = async () => {
    setAuthError(null);
    setShowPasswordFields(false);
    const domain = emailDomain(email);
    if (!domain) {
      setAuthError('Enter a valid email address.');
      return;
    }
    setIsProcessing(true);
    try {
      const providerId = await fetchSsoProvider({ domain });
      if (providerId) {
        await signInWithRedirect(auth, new OAuthProvider(providerId)); // full id, no re-prefix
        return;
      }
      setShowPasswordFields(true);
    } catch (error: any) {
      setAuthError(error?.message || 'Unable to check enterprise SSO. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Manual Google Sign-In
  const handleGoogleSignIn = async () => {
    try {
      setAuthError(null);
      setIsProcessing(true);
      console.log('🚀 Starting manual Google sign-in...');

      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.setCustomParameters({ prompt: 'select_account' });

      const result = await signInWithPopup(auth, provider);
      const userEmail = result.user.email;

      logger.debug('✅ MANUAL Google sign-in SUCCESS:', userEmail);

      // Check if email is in allowlist (client-side validation for UX)
      // Note: Backend will enforce this too, this is just for better UX
      if (!isEmailAllowed(userEmail)) {
        console.error('❌ Email not in allowlist');
        setAuthError(getUnauthorizedMessage(userEmail || undefined));
        // Sign out the user since they're not authorized
        await auth.signOut();
        setIsProcessing(false);
        return;
      }

      // AuthContext will automatically detect this via onAuthStateChanged
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('❌ Google sign-in failed:', error.code, error.message);

      // Handle specific error codes from Firebase Auth blocking functions
      if (error.code === 'auth/unauthorized-continue-uri' ||
          error.code === 'auth/operation-not-allowed' ||
          error.message?.includes('permission-denied')) {
        setAuthError('Your email is not authorized to access this application. Please contact an administrator.');
      } else {
        setAuthError(`Google sign-in failed: ${error.message}`);
      }
      setIsProcessing(false);
    }
  };

  // Microsoft Sign-In (OAuth via Azure AD / Microsoft Entra ID)
  // Tenant 'common' lets both work/school (Azure AD) and personal Microsoft
  // accounts sign in. Allowlist still gates afterwards.
  const handleMicrosoftSignIn = async () => {
    try {
      setAuthError(null);
      setIsProcessing(true);

      const provider = new OAuthProvider('microsoft.com');
      provider.addScope('email');
      provider.setCustomParameters({ prompt: 'select_account', tenant: 'common' });

      const result = await signInWithPopup(auth, provider);
      const userEmail = result.user.email;

      if (!isEmailAllowed(userEmail)) {
        setAuthError(getUnauthorizedMessage(userEmail || undefined));
        await auth.signOut();
        setIsProcessing(false);
        return;
      }

      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('Microsoft sign-in failed:', error.code, error.message);
      if (error.code === 'auth/operation-not-allowed') {
        setAuthError('Microsoft sign-in is not yet configured. Please contact an administrator.');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setAuthError('An account with this email already exists using a different sign-in method. Try Google sign-in or the email/password option.');
      } else if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        // User dismissed the popup — no error toast needed
      } else {
        setAuthError(`Microsoft sign-in failed: ${error.message}`);
      }
      setIsProcessing(false);
    }
  };

  // Email-link (passwordless) sign-in — sends a one-time link to the user's email.
  // Useful for customers without Google accounts (e.g. Microsoft / corporate email).
  const handleSendSignInLink = async () => {
    if (!email) {
      setAuthError('Please enter your email');
      return;
    }
    if (!isEmailAllowed(email)) {
      setAuthError(getUnauthorizedMessage(email));
      return;
    }

    try {
      setAuthError(null);
      setIsProcessing(true);

      await sendSignInLinkToEmail(auth, email, {
        url: `${window.location.origin}/finishSignIn`,
        handleCodeInApp: true,
      });

      window.localStorage.setItem('emailForSignIn', email);
      setLinkSentTo(email);
    } catch (error: any) {
      console.error('Send sign-in link failed:', error.code, error.message);
      let msg = error.message || 'Failed to send sign-in link';
      if (error.code === 'auth/invalid-email') {
        msg = 'Invalid email address.';
      } else if (error.code === 'auth/operation-not-allowed') {
        msg = 'Email-link sign-in is not enabled. Please contact an administrator.';
      } else if (error.code === 'auth/unauthorized-continue-uri') {
        msg = 'Sign-in link redirect is not authorized. Please contact an administrator.';
      }
      setAuthError(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Email/Password Sign-In
  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setAuthError('Please enter both email and password');
      return;
    }

    // Client-side allowlist check (for better UX)
    // Backend will enforce this too via Cloud Functions
    if (!isEmailAllowed(email)) {
      setAuthError(getUnauthorizedMessage(email));
      return;
    }

    try {
      setAuthError(null);
      setIsProcessing(true);
      console.log('🚀 Starting email sign-in...');

      if (isSignUp) {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        logger.debug('✅ Sign-up SUCCESS:', result.user.email);
      } else {
        const result = await signInWithEmailAndPassword(auth, email, password);
        logger.debug('✅ Sign-in SUCCESS:', result.user.email);
      }

      // AuthContext will automatically detect this
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('❌ Email auth failed:', error.code, error.message);

      // User-friendly error messages
      let errorMessage = error.message;

      // Handle Firebase Auth blocking function errors
      if (error.code === 'auth/unauthorized-continue-uri' ||
          error.code === 'auth/operation-not-allowed' ||
          error.message?.includes('permission-denied')) {
        errorMessage = 'Your email is not authorized to access this application. Please contact an administrator.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email. Please sign up.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please sign in.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      }

      setAuthError(errorMessage);
      setIsProcessing(false);
    }
  };

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Decorative background elements */}
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl" />

      {/* Main content */}
      <div className="relative container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <img src="/logo.svg" alt="Precepi" className="h-16 md:h-20 mx-auto mb-3" />
            <h1 className="text-4xl md:text-5xl font-bold text-[#404040] tracking-tight">
              Precepi
            </h1>
            <p className="text-sm font-medium text-slate-500 tracking-widest uppercase mt-1">
              AI powered precedent search for strategic labeling
            </p>
            <p className="mt-4 text-lg text-slate-600">
              Sign in to continue
            </p>
          </div>

          {/* Login card */}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur opacity-20" />
            <div className="relative bg-white rounded-xl shadow-xl shadow-slate-200/50 p-8">

              {/* Error message */}
              {authError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg text-red-700">
                  <div className="flex items-start gap-3">
                    <span className="text-red-400 mt-0.5">⚠</span>
                    <div>
                      <p className="font-medium">Authentication Error</p>
                      <p className="text-sm mt-1 text-red-600">{authError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Email-link success state */}
              {linkSentTo && (
                <div className="mb-6 p-4 bg-green-50 border border-green-100 rounded-lg text-green-800">
                  <div className="flex items-start gap-3">
                    <span className="text-green-500 mt-0.5">✓</span>
                    <div>
                      <p className="font-medium">Check your email</p>
                      <p className="text-sm mt-1 text-green-700">
                        We sent a sign-in link to <span className="font-medium">{linkSentTo}</span>.
                        Click it to finish signing in. The link expires in about an hour.
                      </p>
                      <button
                        type="button"
                        onClick={() => setLinkSentTo(null)}
                        className="text-sm text-green-700 underline mt-2 hover:text-green-900"
                      >
                        Use a different method
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Google Sign-In Button */}
              <button
                onClick={handleGoogleSignIn}
                disabled={isProcessing}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-slate-200 rounded-lg hover:border-indigo-300 hover:bg-slate-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-6"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="font-medium text-slate-700">
                  {isProcessing ? 'Signing in...' : 'Continue with Google'}
                </span>
              </button>

              {/* Microsoft Sign-In Button */}
              <button
                onClick={handleMicrosoftSignIn}
                disabled={isProcessing}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-slate-200 rounded-lg hover:border-indigo-300 hover:bg-slate-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-6"
              >
                <svg className="w-5 h-5" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#f25022" d="M1 1h10v10H1z" />
                  <path fill="#7fba00" d="M12 1h10v10H12z" />
                  <path fill="#00a4ef" d="M1 12h10v10H1z" />
                  <path fill="#ffb900" d="M12 12h10v10H12z" />
                </svg>
                <span className="font-medium text-slate-700">
                  {isProcessing ? 'Signing in...' : 'Continue with Microsoft'}
                </span>
              </button>

              {/* Divider */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-slate-500">Or continue with email</span>
                </div>
              </div>

              {/* Email-first Continue + (once revealed) legacy Email/Password Form.
                  A single-text-field form with no submit-type control still
                  submits implicitly on Enter — route the submit itself
                  through the same state check as the button, so keyboard
                  users hit handleContinue too, not just mouse clicks. */}
              <form
                onSubmit={(e) => {
                  if (showPasswordFields) {
                    void handleEmailSignIn(e);
                  } else {
                    e.preventDefault();
                    void handleContinue();
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="you@example.com"
                    disabled={isProcessing}
                  />
                </div>

                {!showPasswordFields && (
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? 'Checking…' : 'Continue'}
                  </button>
                )}

                {showPasswordFields && (
                  <>
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="••••••••"
                        disabled={isProcessing}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isProcessing}
                      className="w-full bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProcessing ? 'Processing...' : (isSignUp ? 'Sign Up' : 'Sign In')}
                    </button>

                    <button
                      type="button"
                      onClick={handleSendSignInLink}
                      disabled={isProcessing}
                      className="w-full border-2 border-indigo-200 text-indigo-700 px-4 py-3 rounded-lg hover:bg-indigo-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProcessing ? 'Sending…' : 'Email me a sign-in link'}
                    </button>

                    <button
                      type="button"
                      onClick={() => setIsSignUp(!isSignUp)}
                      disabled={isProcessing}
                      className="w-full text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors disabled:opacity-50"
                    >
                      {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
                    </button>
                  </>
                )}
              </form>

              {/* Back to home link */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/')}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                >
                  ← Back to home
                </button>
              </div>
            </div>
          </div>

          {/* Footer info */}
          <div className="mt-8 text-center text-sm text-slate-500">
            <p>
              By signing in, you agree to our Terms of Service and Privacy
              Policy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
